/**
 * F-023 — Procedural planet textures.
 *
 * Generates per-tier color + normal maps procedurally so we ship no
 * binary assets (zero asset licensing). Each tier gets a distinct
 * pattern: dwarf = pebble dust, asteroid = pitted rock, terrestrial =
 * earth-like swirls, etc. Normal maps add surface relief under the
 * directional sun (F-002).
 *
 * Cached per tier so repeated planet creation is cheap.
 */

import {
  CanvasTexture,
  DataTexture,
  RGBAFormat,
  type Texture,
} from "three";

import { getTier, maxTierIndex } from "../game/tier.ts";

const TEXTURE_SIZE = 256;

type RGB = readonly [number, number, number];
type PaintStyle = "cratered" | "smooth" | "banded" | "earthy" | "stormy";

interface ProceduralRule {
  base: RGB;
  style: PaintStyle;
  /** Domain-specific noise frequency. */
  noiseScale: number;
  /** Brightness jitter range (0..1). */
  jitter: number;
  /** Optional accent color used by banded / earthy styles. */
  accent?: RGB;
  /** For banded — visible stripe count. */
  stripeCount?: number;
}

// Per-tier rules — 9 distinct hues, 1:1 aligned with src/game/tier.ts.
//   0 meteor-shard · dark grey-brown rocky
//   1 small-meteor · brown-red rocky
//   2 meteor       · classic rust meteor
//   3 comet        · bright icy cyan
//   4 asteroid     · violet-indigo banded
//   5 moon         · silver-pearl with craters
//   6 planet       · forest emerald earthy
//   7 protostar    · coral-orange burning
//   8 star         · sun yellow goal
const TIER_RULES: ReadonlyArray<ProceduralRule> = [
  // Meteor shard — warm light-tan chunk; bright enough to read against
  // the deep navy backdrop. Cratered detail keeps it rocky.
  { base: [168, 154, 124], style: "cratered", noiseScale: 7, jitter: 0.18 },
  // Small meteor — warmer brown-red.
  { base: [157,  99,  71], style: "cratered", noiseScale: 6, jitter: 0.16 },
  // Meteor — iconic rust orange-red.
  { base: [200,  85,  61], style: "cratered", noiseScale: 6, jitter: 0.14 },
  // Comet — bright icy cyan, almost smooth (clean cartoon).
  { base: [108, 212, 255], style: "smooth",   noiseScale: 5, jitter: 0.08 },
  // Asteroid — violet-indigo banded swirl.
  { base: [108,  90, 214], style: "banded",   noiseScale: 4, jitter: 0.10, accent: [70, 60, 130], stripeCount: 4 },
  // Moon — silver-pearl with crater detail.
  { base: [192, 188, 208], style: "cratered", noiseScale: 7, jitter: 0.10 },
  // Planet — forest emerald earthy world.
  { base: [ 58, 156,  90], style: "earthy",   noiseScale: 6, jitter: 0.12, accent: [42, 124, 70] },
  // Protostar — coral-orange burning surface.
  { base: [255, 122,  58], style: "smooth",   noiseScale: 5, jitter: 0.18 },
  // Star — sun yellow with subtle surface detail.
  { base: [255, 216,  74], style: "smooth",   noiseScale: 4, jitter: 0.10 },
];

// Cheap deterministic pseudo-noise — no PRNG dependency.
function hash(x: number, y: number): number {
  const s = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

function smoothNoise(x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const v00 = hash(x0, y0);
  const v10 = hash(x0 + 1, y0);
  const v01 = hash(x0, y0 + 1);
  const v11 = hash(x0 + 1, y0 + 1);
  const top = v00 * (1 - fx) + v10 * fx;
  const bot = v01 * (1 - fx) + v11 * fx;
  return top * (1 - fy) + bot * fy;
}

function fbm(x: number, y: number, octaves = 4): number {
  let total = 0;
  let amp = 1;
  let max = 0;
  for (let i = 0; i < octaves; i += 1) {
    total += smoothNoise(x, y) * amp;
    max += amp;
    x *= 2;
    y *= 2;
    amp *= 0.5;
  }
  return total / max;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function blend(base: RGB, accent: RGB, mix: number): RGB {
  return [
    lerp(base[0], accent[0], mix),
    lerp(base[1], accent[1], mix),
    lerp(base[2], accent[2], mix),
  ];
}

function paintCratered(rule: ProceduralRule, x: number, y: number): RGB {
  // Bumpy noise: dark spots where noise is low, bright rims where high.
  const n = fbm(x * rule.noiseScale, y * rule.noiseScale, 3);
  const offset = (n - 0.5) * 2 * rule.jitter;
  // Crater overlay — sharp dark dots at high-frequency noise minima.
  const crater = smoothNoise(x * rule.noiseScale * 1.8, y * rule.noiseScale * 1.8);
  const craterTerm = crater < 0.18 ? -0.35 : 0;
  return [
    rule.base[0] * (1 + offset + craterTerm),
    rule.base[1] * (1 + offset + craterTerm),
    rule.base[2] * (1 + offset + craterTerm),
  ];
}

function paintSmooth(rule: ProceduralRule, x: number, y: number): RGB {
  const n = smoothNoise(x * rule.noiseScale, y * rule.noiseScale);
  const offset = (n - 0.5) * 2 * rule.jitter;
  return [
    rule.base[0] * (1 + offset),
    rule.base[1] * (1 + offset),
    rule.base[2] * (1 + offset),
  ];
}

function paintBanded(rule: ProceduralRule, x: number, y: number): RGB {
  const stripes = rule.stripeCount ?? 6;
  // Horizontal sin-stripes blended via accent color.
  const stripe = (Math.sin(y * Math.PI * stripes) + 1) * 0.5;
  // Light per-band wobble so stripes aren't perfectly straight.
  const wobble = smoothNoise(x * rule.noiseScale * 2, y * rule.noiseScale * 0.5) * 0.4;
  const mix = Math.max(0, Math.min(1, stripe * 0.7 + wobble * 0.3));
  const accent = rule.accent ?? rule.base;
  const blended = blend(rule.base, accent, mix);
  const noiseJitter = (smoothNoise(x * rule.noiseScale * 4, y * rule.noiseScale * 4) - 0.5) * rule.jitter;
  return [
    blended[0] * (1 + noiseJitter),
    blended[1] * (1 + noiseJitter),
    blended[2] * (1 + noiseJitter),
  ];
}

function paintEarthy(rule: ProceduralRule, x: number, y: number): RGB {
  // Threshold fbm — anything above 0.55 is "land", else ocean.
  const n = fbm(x * rule.noiseScale, y * rule.noiseScale, 4);
  const accent = rule.accent ?? rule.base;
  if (n > 0.55) {
    // Land — green accent + slight altitude variation.
    const alt = (n - 0.55) / 0.45;
    return [
      accent[0] * (1 - 0.1 * alt),
      accent[1] * (1 - 0.05 * alt),
      accent[2] * (1 - 0.2 * alt),
    ];
  }
  // Ocean — base blue with slight depth variation.
  const depth = (0.55 - n) / 0.55;
  return [
    rule.base[0] * (1 - 0.15 * depth),
    rule.base[1] * (1 - 0.1 * depth),
    rule.base[2] * (1 + 0.05 * depth),
  ];
}

function paintStormy(rule: ProceduralRule, x: number, y: number): RGB {
  // Mars-style basalt patches — fbm with darker accent in the lows.
  const n = fbm(x * rule.noiseScale, y * rule.noiseScale, 3);
  const accent = rule.accent ?? rule.base;
  const mix = Math.pow(1 - n, 1.6);
  const blended = blend(rule.base, accent, mix * 0.7);
  const jitter = (smoothNoise(x * rule.noiseScale * 3, y * rule.noiseScale * 3) - 0.5) * rule.jitter;
  return [
    blended[0] * (1 + jitter),
    blended[1] * (1 + jitter),
    blended[2] * (1 + jitter),
  ];
}

function paintColorBytes(rule: ProceduralRule): Uint8ClampedArray {
  const data = new Uint8ClampedArray(TEXTURE_SIZE * TEXTURE_SIZE * 4);
  for (let y = 0; y < TEXTURE_SIZE; y += 1) {
    const v = y / TEXTURE_SIZE;
    for (let x = 0; x < TEXTURE_SIZE; x += 1) {
      const u = x / TEXTURE_SIZE;
      let rgb: RGB;
      switch (rule.style) {
        case "cratered":
          rgb = paintCratered(rule, u, v);
          break;
        case "smooth":
          rgb = paintSmooth(rule, u, v);
          break;
        case "banded":
          rgb = paintBanded(rule, u, v);
          break;
        case "earthy":
          rgb = paintEarthy(rule, u, v);
          break;
        case "stormy":
          rgb = paintStormy(rule, u, v);
          break;
      }
      const i = (y * TEXTURE_SIZE + x) * 4;
      data[i] = Math.max(0, Math.min(255, Math.round(rgb[0])));
      data[i + 1] = Math.max(0, Math.min(255, Math.round(rgb[1])));
      data[i + 2] = Math.max(0, Math.min(255, Math.round(rgb[2])));
      data[i + 3] = 255;
    }
  }
  return data;
}

function paintCartoonDetail(
  ctx: CanvasRenderingContext2D,
  rule: ProceduralRule,
): void {
  // Drawn cartoon detail layer — deliberate paths on top of the
  // procedural base. This is what pulls each tier from "noise-y blob"
  // toward "illustrated planet."
  ctx.save();
  switch (rule.style) {
    case "cratered":
    case "smooth": {
      // A few drawn craters / highlights — small dark circles with
      // bright rim (cratered) or just bright spec-highlight dots
      // (smooth).
      const cratered = rule.style === "cratered";
      const count = cratered ? 8 : 4;
      const seed = rule.noiseScale * 17;
      for (let i = 0; i < count; i += 1) {
        const u = (Math.sin(seed + i * 1.7) + 1) * 0.5;
        const v = (Math.cos(seed + i * 2.3) + 1) * 0.5;
        const r = TEXTURE_SIZE * (cratered ? 0.04 + (i % 3) * 0.012 : 0.02);
        const cx = u * TEXTURE_SIZE;
        const cy = v * TEXTURE_SIZE;
        if (cratered) {
          ctx.fillStyle = "rgba(0,0,0,0.32)";
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255,0.30)";
          ctx.lineWidth = TEXTURE_SIZE * 0.006;
          ctx.beginPath();
          ctx.arc(cx, cy, r * 0.92, Math.PI * 1.1, Math.PI * 1.9);
          ctx.stroke();
        } else {
          ctx.fillStyle = "rgba(255,255,255,0.25)";
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      // Soft shaded vignette — darker bottom-right, lighter top-left.
      // Adds drawn shading without breaking the cel banding.
      const grad = ctx.createRadialGradient(
        TEXTURE_SIZE * 0.30, TEXTURE_SIZE * 0.30, TEXTURE_SIZE * 0.10,
        TEXTURE_SIZE * 0.65, TEXTURE_SIZE * 0.70, TEXTURE_SIZE * 0.85,
      );
      grad.addColorStop(0, "rgba(255,255,255,0.10)");
      grad.addColorStop(0.55, "rgba(0,0,0,0.00)");
      grad.addColorStop(1, "rgba(0,0,0,0.18)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
      break;
    }
    case "earthy": {
      // Soft cloud wisps over the procedural land/ocean pattern.
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      const wisps: ReadonlyArray<readonly [number, number, number, number]> = [
        [0.30, 0.25, 0.18, 0.05],
        [0.65, 0.55, 0.16, 0.04],
        [0.25, 0.75, 0.14, 0.04],
      ];
      for (const [u, v, w, h] of wisps) {
        ctx.beginPath();
        ctx.ellipse(
          u * TEXTURE_SIZE,
          v * TEXTURE_SIZE,
          (w / 2) * TEXTURE_SIZE,
          (h / 2) * TEXTURE_SIZE,
          0,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
      break;
    }
    case "banded":
    case "stormy": {
      // Subtle horizontal band edges — a hint of "drawn line" between
      // gas-giant zones.
      ctx.strokeStyle = "rgba(0,0,0,0.18)";
      ctx.lineWidth = TEXTURE_SIZE * 0.004;
      const bands = rule.stripeCount ?? 5;
      for (let i = 1; i < bands; i += 1) {
        const y = (i / bands) * TEXTURE_SIZE;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(TEXTURE_SIZE, y);
        ctx.stroke();
      }
      break;
    }
  }
  ctx.restore();
}

function buildCanvasTexture(rule: ProceduralRule): CanvasTexture | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const bytes = paintColorBytes(rule);
  const img = ctx.createImageData(TEXTURE_SIZE, TEXTURE_SIZE);
  img.data.set(bytes);
  ctx.putImageData(img, 0, 0);
  paintCartoonDetail(ctx, rule);
  return new CanvasTexture(canvas);
}

function buildDataTexture(rule: ProceduralRule): DataTexture {
  const bytes = new Uint8Array(paintColorBytes(rule));
  const tex = new DataTexture(bytes, TEXTURE_SIZE, TEXTURE_SIZE, RGBAFormat);
  tex.needsUpdate = true;
  return tex;
}

const cache = new Map<number, Texture>();

export function planetTexture(tierIndex: number): Texture {
  if (tierIndex < 0 || tierIndex > maxTierIndex()) {
    throw new Error(`planetTexture: out-of-range tier ${tierIndex}`);
  }
  const cached = cache.get(tierIndex);
  if (cached) return cached;
  const rule = TIER_RULES[tierIndex] ?? TIER_RULES[0]!;
  const texture =
    buildCanvasTexture(rule) ?? buildDataTexture(rule);
  cache.set(tierIndex, texture);
  return texture;
}

export function clearPlanetTextureCache(): void {
  for (const tex of cache.values()) tex.dispose();
  cache.clear();
}

/** Touch every tier so consumers can pre-warm before the first render. */
export function preloadAllPlanetTextures(): void {
  for (let i = 0; i <= maxTierIndex(); i += 1) {
    planetTexture(i);
  }
}

/** Used by the render layer to keep tint coherent with TIER_TABLE.color. */
export function planetBaseColor(tierIndex: number): number {
  return getTier(tierIndex).color;
}

export const __testing = { TEXTURE_SIZE, TIER_RULES };
